import {
  useCallback,
  useMemo,
  useState,
  type ClipboardEvent,
  type DragEvent,
} from 'react';
import {
  getFilesFromTransfer,
  transferHasFiles,
} from 'utils/getFilesFromTransfer';
import { useWindowEvent } from 'utils/hooks/useWindowEvent';

// One hook for every way a user hands us files: spread `dropProps` for
// drag-and-drop (with `isDragging` for the highlight/overlay) and
// `pasteProps` for clipboard paste — on the input box itself or any
// wrapping element. `windowPaste: true` instead catches pastes anywhere on
// the page (no focus needed — the Inbox chat pattern). Text drags/pastes
// stay native.
export const useFileDrop = (
  onFiles?: (files: File[]) => void,
  { windowPaste = false }: { windowPaste?: boolean } = {},
) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const onDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!onFiles || !transferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    },
    [onFiles],
  );

  const onDragLeave = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!onFiles) return;
      e.preventDefault();
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        setIsDragging(false);
      }
    },
    [onFiles],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!onFiles || !transferHasFiles(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = getFilesFromTransfer(e.dataTransfer);
      if (files.length) onFiles(files);
    },
    [onFiles],
  );

  const onPaste = useCallback(
    (e: ClipboardEvent<HTMLElement>) => {
      if (!onFiles) return;
      const files = getFilesFromTransfer(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      onFiles(files);
    },
    [onFiles],
  );

  useWindowEvent(
    'paste',
    (e: globalThis.ClipboardEvent) => {
      // defaultPrevented = an element-level pasteProps handler (or other
      // paste UI) already claimed this event — don't add the files twice.
      if (!windowPaste || !onFiles || e.defaultPrevented) return;
      const files = getFilesFromTransfer(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      onFiles(files);
    },
    [windowPaste, onFiles],
  );

  const dropProps = useMemo(
    () => ({ onDragOver, onDragLeave, onDrop }),
    [onDragOver, onDragLeave, onDrop],
  );

  const pasteProps = useMemo(() => ({ onPaste }), [onPaste]);

  return { isDragging: isDragging && !!onFiles, dropProps, pasteProps };
};

// App-root safety net: a file dropped anywhere outside a drop target would
// otherwise make the browser open the file and replace the SPA. Drop targets
// stopPropagation in their handlers, so this only sees unhandled drags.
export const useFileDropNavigationGuard = () => {
  useWindowEvent('dragover', e => {
    if (!transferHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
  });
  useWindowEvent('drop', e => {
    if (transferHasFiles(e.dataTransfer)) e.preventDefault();
  });
};
